import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api';

// RBAC constants (must match backend config/rbac.js)
const HARD_ADMIN_SECTIONS = ['subid', 'decline', 'teams', 'users', 'logs'];
const BILLING_BLOCKED_ROLES = ['head_buyer', 'team_lead', 'buyer'];
const STANDARD_SECTIONS = ['dashboard', 'analytics', 'statistics', 'quality', 'kpi', 'keitaro-manager', 'traffic-shield', 'security'];

const CM_PERM_TO_SECTION = {
  dashboard_view: 'analytics',
  quality_view: 'quality',
  stats_view: 'statistics',
  kpi_view: 'kpi', kpi_manage: 'kpi',
  offers_view: 'keitaro-manager', offers_edit: 'keitaro-manager',
  billing_view: 'billing', billing_edit: 'billing',
  traffic_shield_view: 'traffic-shield', traffic_shield_manage: 'traffic-shield',
};

const CM_EDIT_PERMS = {
  'billing': 'billing_edit',
  'keitaro-manager': 'offers_edit',
  'kpi': 'kpi_manage',
  'traffic-shield': 'traffic_shield_manage',
};

const ROLE_LABELS = {
  admin: 'Admin',
  head_buyer: 'Head Buyer',
  team_lead: 'Team Lead',
  buyer: 'Buyer',
  custom_manager: 'Custom Manager',
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Impersonation state
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const saved = localStorage.getItem('impersonation');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  useEffect(() => {
    checkAuth();
  }, []);

  // Persist impersonation state
  useEffect(() => {
    if (impersonation) {
      localStorage.setItem('impersonation', JSON.stringify(impersonation));
    } else {
      localStorage.removeItem('impersonation');
    }
  }, [impersonation]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.me();
      const userData = response.data.user;
      setUser(userData);

      // Restore impersonation state from /me response
      if (userData.is_impersonation) {
        const savedImp = localStorage.getItem('impersonation');
        if (!savedImp) {
          setImpersonation({
            active: true,
            originalToken: null, // Lost on refresh, but we can still stop
            targetName: userData.name,
            targetRole: userData.role,
            startedAt: new Date().toISOString(),
            sessionId: userData.impersonation_session_id
          });
        }
      }
    } catch (error) {
      // If 2FA required, don't clear - let Login handle it
      if (error.response?.status === 403 && error.response?.data?.error === '2fa_required') {
        setLoading(false);
        return;
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const data = response.data;

    if (data.twoFaRequired) {
      // Store temp token for 2FA validation
      localStorage.setItem('token', data.token);
      return { twoFaRequired: true, user: data.user };
    }

    // Normal login (no 2FA)
    localStorage.setItem('token', data.token);
    try { localStorage.setItem('user', JSON.stringify(data.user)); } catch (e) {}
    setUser(data.user);

    return data.user;
  };

  const validate2FA = async (code, recoveryCode) => {
    const response = await authAPI.validate2FA(code, recoveryCode);
    const { token, user: userData, recoveryCodesRemaining } = response.data;

    localStorage.setItem('token', token);
    try { localStorage.setItem('user', JSON.stringify(userData)); } catch (e) {}
    setUser(userData);

    return { user: userData, recoveryCodesRemaining };
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // ignore
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonation');
    setUser(null);
    setImpersonation(null);
  };

  const startImpersonation = useCallback(async (targetUserId, reason, totpCode) => {
    // Save current admin token
    const currentToken = localStorage.getItem('token');
    const currentUser = user;

    const response = await authAPI.impersonate(targetUserId, reason, totpCode);
    const { token, user: targetUser, impersonation: impData } = response.data;

    // Set new token and user
    localStorage.setItem('token', token);
    try { localStorage.setItem('user', JSON.stringify(targetUser)); } catch (e) {}
    setUser(targetUser);

    setImpersonation({
      active: true,
      originalToken: currentToken,
      originalUser: currentUser,
      targetName: impData.targetName,
      targetEmail: impData.targetEmail,
      targetRole: impData.targetRole,
      startedAt: impData.startedAt,
      sessionId: impData.sessionId
    });

    return targetUser;
  }, [user]);

  const stopImpersonation = useCallback(async () => {
    try {
      const response = await authAPI.stopImpersonation();
      const { token, user: adminUser } = response.data;

      localStorage.setItem('token', token);
      try { localStorage.setItem('user', JSON.stringify(adminUser)); } catch (e) {}
      setUser(adminUser);
      setImpersonation(null);

      return adminUser;
    } catch (err) {
      // Fallback: restore original token if available
      if (impersonation?.originalToken) {
        localStorage.setItem('token', impersonation.originalToken);
        if (impersonation.originalUser) {
          setUser(impersonation.originalUser);
          try { localStorage.setItem('user', JSON.stringify(impersonation.originalUser)); } catch (e) {}
        }
        setImpersonation(null);
        // Re-fetch user data
        try {
          const meRes = await authAPI.me();
          setUser(meRes.data.user);
        } catch (e) { /* ignore */ }
      }
      throw err;
    }
  }, [impersonation]);

  const isImpersonating = !!(impersonation?.active);

  const hasRole = (...roles) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return roles.includes(user.role);
  };

  const canAccess = (section) => {
    if (!user) return false;
    const role = user.role;

    if (role === 'admin') return true;
    if (section === 'dashboard') return true;
    if (HARD_ADMIN_SECTIONS.includes(section)) return false;
    if (section === 'billing' && BILLING_BLOCKED_ROLES.includes(role)) return false;

    if (['head_buyer', 'team_lead', 'buyer'].includes(role)) {
      return STANDARD_SECTIONS.includes(section);
    }

    if (role === 'custom_manager') {
      const perms = user.custom_permissions || [];
      return perms.some(p => CM_PERM_TO_SECTION[p] === section);
    }

    return false;
  };

  const canEdit = (section) => {
    if (!user) return false;
    const role = user.role;

    if (role === 'admin') return true;
    if (HARD_ADMIN_SECTIONS.includes(section)) return false;
    if (section === 'billing' && BILLING_BLOCKED_ROLES.includes(role)) return false;

    if (['head_buyer', 'team_lead'].includes(role)) {
      return STANDARD_SECTIONS.includes(section);
    }

    if (role === 'buyer') return false;

    if (role === 'custom_manager') {
      const perms = user.custom_permissions || [];
      const editPerm = CM_EDIT_PERMS[section];
      if (editPerm) return perms.includes(editPerm);
      return perms.some(p => CM_PERM_TO_SECTION[p] === section);
    }

    return false;
  };

  const roleLabel = user ? (ROLE_LABELS[user.role] || user.role) : '';

  const value = {
    user,
    loading,
    login,
    validate2FA,
    logout,
    hasRole,
    canAccess,
    canEdit,
    roleLabel,
    isAdmin: user?.role === 'admin',
    isAuthenticated: !!user,
    // Impersonation
    impersonation,
    isImpersonating,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
